import { useEffect, useRef } from 'react'
import type { ChartManager } from '../core/ChartManager'
import type { ChartSettings } from '../components/SettingsModal'
import type { SplitEvent } from '../types/splits'
import { SPLIT_MARKERS_NAME, type SplitMarkersConfig } from '../utils/splitMarkersIndicator'
import { CANDLE_PANE_ID } from '../types/chart'
import { sameInputs } from '../utils/sameInputs'

type Chart = ReturnType<ChartManager['getChart']>

/**
 * Mounts / updates / removes the SPLIT_MARKERS system indicator on the candle
 * pane. Exact clone of the `useEarningsMarkers` lifecycle: remove-first for a
 * clean slate, then (re-)create with the splits array + session timezone in
 * `extendData`. klinecharts has no "update extendData in place" API for a
 * figure-less indicator, so the effect re-creates on any change of splits /
 * timezone / toggle - cheap (the badges redraw from extendData every frame
 * anyway).
 *
 * The indicator is left OFF when the toggle is off or no splits are supplied,
 * so charts with no split history pay nothing.
 */
export function useSplitMarkers(
  chartInstance: Chart | null,
  settings: ChartSettings,
  splits: SplitEvent[] | undefined,
): void {
  const showSplits = settings.showSplits
  const timezone = settings.timezone
  // Stable identity for the deps array without re-running on array reference
  // churn: the splits payload is small, so a JSON key is fine here.
  const splitsKey = splits ? JSON.stringify(splits) : ''
  const appliedRef = useRef<unknown[] | null>(null)

  useEffect(() => {
    // A new splits array with the same content must not re-create the indicator.
    const inputs = [chartInstance, showSplits, timezone, splitsKey]
    if (sameInputs(appliedRef.current, inputs)) return
    appliedRef.current = inputs
    if (!chartInstance) return

    // Always remove first to ensure a clean state on any change.
    chartInstance.removeIndicator(CANDLE_PANE_ID, SPLIT_MARKERS_NAME)

    if (showSplits && splits && splits.length > 0) {
      chartInstance.createIndicator(
        {
          name: SPLIT_MARKERS_NAME,
          extendData: {
            splits,
            timezone,
          } satisfies SplitMarkersConfig,
        } as Parameters<typeof chartInstance.createIndicator>[0],
        true,
        { id: CANDLE_PANE_ID } as Parameters<typeof chartInstance.createIndicator>[2],
      )
    }
  }, [chartInstance, showSplits, timezone, splitsKey, splits])
}
